import React, { useEffect, useContext, useState } from 'react';
import styles from './KanBanView.module.scss';
import DeviceContext from '../context/DeviceContext';
import { KanbanBoard } from '../kanban/KanbanBoard';
import CVLogo from '../assets/CVLogo.svg';
import TrafficLight from '../assets/TrafficLight.svg';
import TomasPP from '../images/Tomas.jpg';
import DavidPP from '../images/David.jpg';
import SelinayPP from '../images/Selinay.jpg';
import { useQuery, gql } from '@apollo/client';
import { GET_PROJECTS_QUERY, GET_TASKS_BY_PROJECT_QUERY } from '../query/gqlQueries.js';
import EditTaskDialog from './EditTaskDialog.js';

export default function KanbanView({ isKanbanViewVisible, setIsKanbanViewVisible }) {
  const username = 'David';
  const { isMobile } = useContext(DeviceContext);
  const [projectName, setprojectName] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [hours, setHours] = useState([0, 0, 0, 0, 0]);
  const [projectHours, setProjectHours] = useState({
    current: 0,
    budgeted: 0,
    available: 0,
    toCompletion: 0
  });
  const taskTypes = ['Admin', '3D', 'Coding', 'Design', 'Learning'];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Filters & Sorting state
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterDueDate, setFilterDueDate] = useState('');
  const [sortBy, setSortBy] = useState(''); // 'priority' | 'dueDate'

  // Fetch projects
  const { data, loading, error } = useQuery(GET_PROJECTS_QUERY, {
    variables: { username }
  });

  // Fetch tasks for the selected project
  const {
    data: tasksData,
    loading: tasksLoading,
    error: tasksError,
    refetch: refetchTasks
  } = useQuery(GET_TASKS_BY_PROJECT_QUERY, {
    variables: { projectName },
    skip: !projectName
  });

  // Calculate task type hours
  useEffect(() => {
    if (tasksData?.getTasksByProject) {
      const hoursByType = taskTypes.map(type =>
        tasksData.getTasksByProject
          .filter(item => item.task.properties.type === type)
          .reduce((sum, item) => sum + (Number(item.task.properties.timeBudget) || 0), 0)
      );
      setHours(hoursByType);
    } else {
      setHours([0, 0, 0, 0, 0]);
    }
  }, [tasksData]);

  // Calculate project hours
  useEffect(() => {
    if (tasksData?.getTasksByProject && projectName && data?.getProjects) {
      const current = tasksData.getTasksByProject.reduce(
        (sum, item) => sum + (Number(item.task.properties.timeSpent) || 0),
        0
      );
      const project = data.getProjects.find(p => p.properties.title === projectName);
      const budgeted = Number(project?.properties.budgetedHours) || 0;
      const available = Math.max(budgeted - current, 0);
      const toCompletion = tasksData.getTasksByProject
        .filter(item => item.status !== 'DONE')
        .reduce((sum, item) => sum + (Number(item.task.properties.timeBudget) || 0), 0);

      setProjectHours({
        current,
        budgeted,
        available,
        toCompletion
      });
    } else {
      setProjectHours({
        current: 0,
        budgeted: 0,
        available: 0,
        toCompletion: 0
      });
    }
  }, [tasksData, projectName, data]);

  // Handle key presses for desktop
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isMobile && isKanbanViewVisible && event.key === 'e') {
        setIsKanbanViewVisible(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, isKanbanViewVisible]);

  // Set default project
  useEffect(() => {
    if (data?.getProjects?.length > 0 && !projectName) {
      setprojectName(data.getProjects[0].properties.title);
    }
  }, [data]);

  const handleClose = () => {
    setIsKanbanViewVisible(false);
  };

  const handleRefresh = () => {
    if (projectName) {
      refetchTasks();
    }
  };

  // Mapping from task status to columnId
  const statusToColumnId = {
    BACKLOG: 'backlog',
    INPROGRESS: 'in-progress',
    VALIDATING: 'validating',
    DONE: 'done'
  };

  // Define column IDs and titles
  const defaultCols = [
    { id: 'backlog', title: 'Backlog' },
    { id: 'in-progress', title: 'In progress' },
    { id: 'validating', title: 'Validating' },
    { id: 'done', title: 'Done' }
  ];

  // Priority ranking
  const priorityRank = {
    urgent: 4,
    high: 3,
    medium: 2,
    med: 2,
    low: 1
  };

  // Filter and sort tasks
  let rawTasks = tasksData?.getTasksByProject || [];

  if (filterAssignee) {
    rawTasks = rawTasks.filter(item => item.task.properties.assignee === filterAssignee);
  }
  if (filterPriority) {
    rawTasks = rawTasks.filter(item => item.task.properties.priority?.toLowerCase() === filterPriority.toLowerCase());
  }
  if (filterDueDate) {
    rawTasks = rawTasks.filter(item => {
      if (!item.task.properties.dueDate) return false;
      const dateStr = new Date(item.task.properties.dueDate).toISOString().split('T')[0];
      return dateStr === filterDueDate;
    });
  }

  if (sortBy === 'priority') {
    rawTasks = [...rawTasks].sort((a, b) => {
      const pA = priorityRank[a.task.properties.priority?.toLowerCase()] || 1;
      const pB = priorityRank[b.task.properties.priority?.toLowerCase()] || 1;
      return pB - pA;
    });
  } else if (sortBy === 'dueDate') {
    rawTasks = [...rawTasks].sort((a, b) => {
      const dA = new Date(a.task.properties.dueDate || '9999-12-31').getTime();
      const dB = new Date(b.task.properties.dueDate || '9999-12-31').getTime();
      return dA - dB;
    });
  }

  // Transform tasks for KanbanBoard
  const tasks = rawTasks.map((item) => ({
    id: item.task.identity,
    identity: item.task.identity,   // needed by EditTaskDialog for update/delete
    columnId: statusToColumnId[item.status] || 'backlog',
    status: item.status,
    content: item.task.properties.title,
    assignee: item.task.properties.assignee,
    properties: item.task.properties
  }));

  // Get top 3 priority tasks
  const topPriorityTasks = tasksData?.getTasksByProject
    ?.slice() // Create a copy to avoid mutating original
    .sort((a, b) => {
      const priorityA = priorityRank[a.task.properties.priority?.toLowerCase()] || 1;
      const priorityB = priorityRank[b.task.properties.priority?.toLowerCase()] || 1;
      return priorityB - priorityA; // Descending order
    })
    .slice(0, 3) || [];

  // Assignee to profile picture mapping
  const assigneeImages = {
    David: DavidPP,
    Tomas: TomasPP,
    Selinay: SelinayPP
  };

  // Priority to color class mapping
  const priorityColors = {
    urgent: styles.red,
    high: styles.orange,
    medium: styles.green,
    med: styles.green,
    low: styles.grey
  };

  return (
    <div className={styles.kanbanFullView}>
      <div className={`${styles.navBar} ${styles.grid}`}>
        <div className={styles.logo}>
          <img src={CVLogo} alt="Logo" />
        </div>
        <div className={styles.title}>
          <div className={styles.projectNameTrigger} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            {projectName || 'Select a project'}
          </div>
          {isDropdownOpen && (
            <ul className={styles.dropdownMenu}>
              {data?.getProjects.map((project) => (
                <li
                  key={project.elementId}
                  onClick={() => {
                    setprojectName(project.properties.title);
                    setIsDropdownOpen(false);
                  }}>
                  {project.properties.title}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={styles.projectHealth}>
          Project Health
          <button className={styles.refreshButton} onClick={handleRefresh} disabled={tasksLoading || !projectName}>
            Refresh Tasks
          </button>
        </div>
        <div className={styles.trafficLight}>
          <div className={styles.closeButton} onClick={handleClose}>
            X
          </div>
          <img src={TrafficLight} />
        </div>
      </div>
      <div className={`${styles.mainGrid} ${styles.grid}`}>
        <div className={styles.rightSubGrid}>
          <div className={styles.taskSplit}>
            <div className={styles.title}>Task Split</div>
            <div className={styles.taskTypesContainer}>
              {taskTypes.map((type, index) => (
                <div key={type} className={styles.taskType}>
                  <div className={styles.barsContainer}>
                    {Array.from({ length: Math.round(hours[index]) }).map((_, barIndex) => (
                      <div key={barIndex} className={styles.bar}></div>
                    ))}
                  </div>
                  <div className={`${styles.taskTypeLabel}`}>{type}</div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.priorityItems}>
            <div className={styles.title}>Priority Items</div>
            {topPriorityTasks.length > 0 ? (
              topPriorityTasks.map((item) => (
                <div key={item.task.identity} className={styles.priorityItem}>
                  <div className={styles.itemTitle}>{item.task.properties.title}</div>
                  <div className={styles.rightItems}>
                    <div className={styles.itemAsignee}>
                      <img
                        src={assigneeImages[item.task.properties.assignee] || DavidPP}
                        alt={item.task.properties.assignee}
                      />
                    </div>
                    <div
                      className={`${styles.itemPriority} ${
                        priorityColors[item.task.properties.priority?.toLowerCase()] || styles.grey
                      }`}
                    ></div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noPriorityTasks}>No priority tasks available</div>
            )}
          </div>
          <div className={styles.hours}>
            <div className={styles.title}>Hours</div>
            <div className={styles.boxGrid}>
              <div className={styles.whiteBoxContainer}>
                <div className={styles.title}>Current</div>
                <div className={styles.hoursValue}>{projectHours.current} hrs</div>
              </div>
              <div className={styles.whiteBoxContainer}>
                <div className={styles.title}>Budgeted</div>
                <div className={styles.hoursValue}>{projectHours.budgeted} hrs</div>
              </div>
              <div className={styles.whiteBoxContainer}>
                <div className={styles.title}>Available</div>
                <div className={styles.hoursValue}>{projectHours.available} hrs</div>
              </div>
              <div className={styles.whiteBoxContainer}>
                <div className={styles.title}>To Completion</div>
                <div className={styles.hoursValue}>{projectHours.toCompletion} hrs</div>
              </div>
            </div>
          </div>
          <div className={styles.kanBanContainer}>
            <div className={styles.filterBar}>
              <select className={styles.filterSelect} value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
                <option value="">All Assignees</option>
                <option value="David">David</option>
                <option value="Selinay">Selinay</option>
                <option value="Tomas">Tomas</option>
              </select>

              <select className={styles.filterSelect} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>

              <input 
                type="date" 
                className={styles.filterInput} 
                value={filterDueDate} 
                onChange={e => setFilterDueDate(e.target.value)} 
                title="Filter by Due Date"
              />

              <select className={styles.filterSelect} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="">No Sorting</option>
                <option value="priority">Sort by Priority</option>
                <option value="dueDate">Sort by Due Date</option>
              </select>

              <button 
                className={styles.clearBtn} 
                onClick={() => { setFilterAssignee(''); setFilterPriority(''); setFilterDueDate(''); setSortBy(''); }}
              >
                Clear Filters
              </button>
            </div>

            {loading || tasksLoading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>Error loading projects: {error.message}</p>
            ) : tasksError ? (
              <p>Error loading tasks: {tasksError.message}</p>
            ) : tasks.length === 0 ? (
              <p>No tasks found for this project.</p>
            ) : (
              <KanbanBoard 
                defaultCols={defaultCols} 
                initialTasks={tasks} 
                refetchTasks={refetchTasks}
                onEditTask={(task) => {
                  setSelectedTask(task);
                  setIsTaskDialogOpen(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
      
      <EditTaskDialog 
        isOpen={isTaskDialogOpen} 
        onClose={() => {
          setIsTaskDialogOpen(false);
          setSelectedTask(null);
          refetchTasks();
        }}
        task={selectedTask}
      />
    </div>
  );
}